// Purpose of This File: Enhancing Product Listing Page (PLP) UI
// This file was specifically created to enhance the User Interface (UI) of the Product Listing Page (PLP).
// The existing product details were minimal, so this new file aligns with the full - fledged UI design for product representation.

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import Image, { ImageProps } from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop } from 'helpers/utils/screensizes';
import useTrack from '../../../product-tile/useTrack';
import { CenterImageCropSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import Prices from '../../../product-tile/prices';
// import Link from 'components/commercetools-ui/atoms/link';
import Link from 'next/link';
import Price from '../Algolia-search-Page/price';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import aa from 'search-insights';
import useLocale from 'helpers/hooks/useLocale';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { useAccount } from 'frontastic';
import Loading from 'components/commercetools-ui/atoms/loading';
import PriceMap from '../AlgoliaPrice/priceMap';
import { useCookies } from 'react-cookie';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';

interface ProductListTileProps {
  product: Product;
  productLogo?: ImageProps;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  onCompareChange?: (product: Product, checked: boolean) => void;
  isCompared?: boolean;
  handleCompareChange: (product: Product) => void;
  setOpen: (b: boolean) => void;
  compareProduct: any;
  badgeData?: any;
  slug?: string;
}

const ProductListTile: FC<ProductListTileProps> = ({
  handleCompareChange,
  product,
  productLogo,
  onClick,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  onCompareChange,
  isCompared = false,
  setOpen,
  compareProduct,
  badgeData,
  slug,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { ref } = useTrack({ product });
  const { _url } = product;
  const urlArray = _url?.split('/') ?? [];
  const catUrl = slug?.split('_')?.[0];
  const pdpUrl = `/${catUrl}/${urlArray[urlArray?.length - 1]}`;
  const variantWithDiscount = useVariantWithDiscount(product?.variants) as Variant;
  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  const [hoveredVariant, setHoveredVariant] = useState<Variant | null>(null); // Initialize with null
  const [selectedColour, setSelectedColour] = useState<string | null>(
    variantWithDiscount ? variantWithDiscount?.attributes?.colour : product?.variants[0]?.attributes?.colour || null,
  );
  const { loggedIn, result } = useAccount();
  const { locale } = useLocale();
  const isProductInCompare = compareProduct?.some((p: any) => p?.productKey === product?.productKey);
  // const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);
  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);
  const mrrpPounds = mrrpPrice / Math.pow(10, 2);
  const pricePounds = price / Math.pow(10, 2);

  // const handleNavigation = (link: string) => {
  //   // window.location.href = link;
  //   // setIsLoading(true);
  //   // const timeoutId = setTimeout(() => {
  //   //   setIsLoading(false);
  //   // }, 1000);

  //   // return () => clearTimeout(timeoutId);
  //   // setIsLoading(true);
  //   requestAnimationFrame(() => {
  //     window.location.href = link;
  //   });
  // };
  // const buttonIsVisible = useMemo(
  //   () => (imageHovered || buttonHovered) && isDesktopSize,
  //   [imageHovered, buttonHovered, isDesktopSize],
  // );

  const handleColourSelect = (variant: Variant, objectId: string) => {
    handleProductSwatchClick(locale, [objectId]);
    setSelectedColour(variant?.attributes?.colour);
    setSelectedVariant(variant);
  };

  const uniqueColorVariants = useMemo(() => {
    const seenColours = new Set();
    return product.variants.filter((variant: Variant) => {
      const colour = variant?.attributes?.colour;
      if (!colour || seenColours.has(colour)) {
        return false;
      }
      seenColours.add(colour);
      return true;
    });
  }, [product.variants]);
  const badgeStyles = {
    BLACKNOVEMBER: 'bg-black text-white h-[22px] w-[95px] md:h-[30px] md:w-[160px]',
    SALE: 'bg-[#E30616] text-white h-[22px] w-[28px] md:h-[30px] md:w-[67px]',
    NEW: 'bg-[#137E57] text-white h-[22px] w-[38px] md:h-[30px] md:w-[68px]',
    MEGADEALS: 'bg-gradient-to-r from-[#272C59] to-[#BD2F48] text-white h-[22px] w-[65px] md:h-[30px] md:w-[112px]',
    CHIRSTMAS: 'bg-[#137E57] text-white h-[22px] w-[65px] md:h-[30px] md:w-[120px]',
    TOPGIFT: 'bg-[#D9BE65] text-black h-[20px] w-[53px] md:h-[30px] md:w-[93px]',
    BANKHOLIDAY: 'bg-[#BD2F48] text-white h-[22px] w-[79px] md:h-[30px] md:w-[134px]',
    BANKHOLIDAYDEAL: 'bg-[#BD2F48] text-white h-[22px] w-[103px] md:h-[30px] md:w-[173px]',
    FREEACCESSORYPACK: 'bg-[#1E1E1E] text-white h-[22px] w-[115px] md:h-[30px] md:w-[193px]',
  };
  const userId = loggedIn ? result?.data?.data?.account?.accountId : localStorage.getItem('__anon_id');
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const handleProductClick =
    (locale: string, productId: string | undefined) => (event: React.MouseEvent<HTMLDivElement>) => {
      // setIsLoading(true);
      const analytics = new AlgoliaAnalytics(locale);
      analytics.handleProductClick(productId);
    };

  const handleProductSwatchClick = (locale: string, objectId: string[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleColourSwatchesClickedInPLP(objectId, userId);
  };
  const isCustomFitProduct = product?.customFit === 'CUSTOM';
  //Newly Updated code for calculating the discount percentage
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price || mrrpPrice < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);

  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);

  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice?.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    const filteredPrice = product?.standalonePrice?.find((price: any) => price?.variantSku === selectedVariant?.sku);

    if (filteredPrice) {
      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
  }, [product?.standalonePrice, selectedVariant?.sku, cookies?.locale_selection?.currency]);

  const cartDiscount: string[] = ((product?.offer as { id: string; description: string }[]) || [])
    ?.map((offer) => offer?.description)
    ?.filter((desc: string) => desc !== '');

  return (
    <>
      {/* {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : ( */}
      <div
        ref={ref}
        className="flex h-full flex-col justify-between border p-8 hover:cursor-pointer sm:p-0 md:border-0"
        // onClick={() => {
        //   handleProductClick(locale, product?.objectId);
        //   handleNavigation(
        //     `${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`,
        //   );
        // }}
      >
        <SmartLink
          // href={isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}
          href={
            isCustomFitProduct
              ? product?._url ?? ''
              : `${product?._url ?? ''}?variant=${
                  selectedVariant?.sku ?? product?.standalonePrice?.[0]?.variantSku ?? ''
                }`
          }
          callback={handleProductClick(locale, product?.objectId)}
          // onClick={(e) => {
          //   // Detect "open in new tab" or non-left-clicks
          //   if (
          //     e.metaKey || // ⌘ Cmd on Mac
          //     e.ctrlKey || // Ctrl on Windows/Linux
          //     e.shiftKey || // Shift+Click
          //     e.button !== 0 // Middle click (button 1), right click (button 2)
          //   ) {
          //     // Let the browser handle it normally
          //     return;
          //   }

          //   // Prevent default only for left-click navigation
          //   e.preventDefault();
          //   handleProductClick(locale, product?.objectId);
          //   handleNavigation(
          //     `${
          //       isCustomFitProduct
          //         ? product?._url
          //         : `${product?._url}?variant=${
          //             selectedVariant?.sku ? selectedVariant?.sku : product?.standalonePrice?.[0]?.variantSku
          //           }`
          //     }`,
          //     // product?._url ?? '',
          //   );
          // }}
        >
          <div className="relative">
            {/* <Link
              link={`${isCustomFitProduct ? pdpUrl : `${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}`}
            > */}
            <div
              className="relative w-full"
              onMouseEnter={() => setImageHovered(true)}
              onMouseLeave={() => setImageHovered(false)}
            >
              <div
                className="relative w-full"
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
              >
                <div className="relative  bg-white">
                  <div className="relative block " style={{ paddingBottom: '122%' }}>
                    <Image
                      src={hoveredVariant?.images?.[0] || selectedVariant.images?.[0] || productLogo?.media?.file}
                      suffix="medium"
                      alt={product.name ?? ''}
                      style={{ objectFit: 'contain', objectPosition: 'center' }}
                      className="w-full rounded-sm group-hover:opacity-75"
                      fill
                    />
                    {typeof discountPercentage === 'number' &&
                      discountPercentage > 0 &&
                      typeof mrrpPoundsDiscount === 'number' &&
                      mrrpPoundsDiscount && (
                        <div className="absolute left-0 top-0 z-10 flex h-[20px] min-w-[39px] items-center justify-center gap-8 self-stretch rounded-[2px] border border-[#686868] bg-white px-6 py-8 sm:left-2 sm:top-2 sm:h-[22px] sm:min-w-[46px]">
                          {/* <div className=" h-[20px] min-w-[39px] lg:h-[22px] lg:min-w-[46px]"> */}
                          <p
                            className={`text-center font-body text-11 font-bold not-italic
                 text-[#686868] sm:text-14 ${
                   isIOS ? 'mb-0 pb-0 pt-4 leading-[30px] sm:leading-[30px]' : 'leading-[12.1px] sm:leading-[15.4px]'
                 }`}
                          >
                            {mrrpPounds < 200
                              ? `-${discountPercentage}%`
                              : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                                  mrrpPoundsDiscount,
                                )}`}
                          </p>
                          {/* </div> */}
                        </div>
                      )}
                    {badgeData?.campaign_Items?.length > 0 && (
                      <div className="absolute right-0 top-0 flex flex-col items-end gap-y-3">
                        {badgeData?.campaign_Items?.map((item: any) =>
                          (badgeStyles as any)[item?.replace(/\s+/g, '')?.toUpperCase()] ? (
                            <div
                              key={item}
                              className={`flex items-center justify-center text-11 md:text-18 ${
                                (badgeStyles as any)[item?.replace(/\s+/g, '')?.toUpperCase()]
                              }`}
                            >
                              <p className="text-center">{item}</p>
                            </div>
                          ) : null,
                        )}
                      </div>
                    )}

                    {cartDiscount?.length > 0 && (
                      <div className="absolute bottom-0 w-full items-center justify-center bg-[#00d195]">
                        <p className="xs:text-16 px-5 py-8 text-center font-body font-medium leading-5  lg:text-18">
                          {cartDiscount?.[0] ?? ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* </Link> */}
            </div>

            {/* <Link link={`${pdpUrl}?variant=${product?.standalonePrice?.[0]?.variantSku}`}> */}
            <div className="mt-4 text-center">
              <div className="relative flex-grow overflow-hidden text-ellipsis pb-[15px]  text-[18px] font-medium leading-[22px] md:text-[18px] md:leading-[22px]">
                {product?.name}
              </div>
            </div>
            {/* </Link> */}
          </div>
        </SmartLink>
        {/* <div className="mb-10 py-7">
        <TrustBox id={product?.variants?.[0]?.sku} showVertical={false} />
      </div> */}

        {/* Price Section */}
        <div className="mt-4 text-center md:mt-6">
          <div className="flex flex-nowrap items-center justify-center text-[32px] font-bold leading-[32px] text-[#1e1e1e]">
            {/* Need to Fix */}
            {/* <Prices price={selectedVariant?.price} discountedPrice={selectedVariant?.discountedPrice} /> */}
            <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
          </div>
        </div>

        {/* As per Client requirement disabling the compare button (03.03.2025) */}

        {/* <div className="mt-6 flex hidden  items-center justify-center py-8">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isProductInCompare}
            onChange={() => {
              setOpen(true), handleCompareChange(product);
            }}
            className="text-primary form-checkbox h-20 w-20 rounded-sm"
          />
          <span className="text-sm md:text-base">Compare</span>
        </label>
      </div> */}

        <div className="mt-8 flex min-h-[84px] flex-wrap justify-center gap-4">
          {uniqueColorVariants.map((variant, index) => (
            <div
              key={index}
              className={`relative inline-block h-[24px] w-[24px] cursor-pointer pl-2  ${
                hoveredVariant === variant ? ' ' : ''
              }`}
              onMouseEnter={() => setHoveredVariant(variant)}
              onMouseLeave={() => setHoveredVariant(null)}
              onClick={() => handleColourSelect(variant, product?.objectId as any)}
              title={variant?.attributes?.colour}
            >
              <CenterImageCropSwatch
                imgUrl={variant?.images?.[0]}
                altText="swatch image"
                sku={variant?.sku}
                isActive={selectedColour === variant?.attributes?.colour}
              />
            </div>
          ))}
        </div>
      </div>
      {/* )} */}
    </>
  );
};

export default ProductListTile;
